import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Users, 
  TrendingUp, 
  ShoppingCart,
  IndianRupee,
  BarChart3,
  RefreshCw,
  Download,
  Filter,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  Store
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Area,
  ScatterChart,
  Scatter,
  AreaChart
} from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const FootfallVsSales = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const { languageConfig } = useLanguage();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (selectedStore) params.store_id = selectedStore;

      const response = await axios.get(`${BACKEND_URL}/api/analytics/footfall-vs-sales`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load Footfall vs Sales analytics');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedStore]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleExport = () => {
    if (!data?.detailed_data) return;
    
    const headers = ['Store ID', 'Date', 'Visitors', 'Sales Value', 'Transactions', 'Conversion Rate', 'Revenue/Visitor', 'Avg Transaction'];
    const csvContent = [
      headers.join(','),
      ...data.detailed_data.map(row => [
        row.store_id,
        row.date,
        row.visitors,
        row.sales_value,
        row.transactions,
        row.conversion_rate,
        row.revenue_per_visitor,
        row.avg_transaction_value
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `footfall_vs_sales_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Data exported successfully');
  };

  const formatCurrency = (value) => {
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value.toFixed(0)}`;
  };

  const formatNumber = (value) => {
    if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="footfall-sales-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const summary = data?.summary || {};
  const chartData = data?.store_chart_data || [];

  return (
    <div className="space-y-6" data-testid="footfall-vs-sales-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            <TranslatedText text="Footfall vs. Sales Analytics" cacheKey="footfallsales:title" />
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Analyze visitor traffic correlation with sales performance" cacheKey="footfallsales:subtitle" />
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            data-testid="refresh-footfall-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            data-testid="export-footfall-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-50 dark:bg-slate-800/50">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
                placeholder="From Date"
                data-testid="date-from-input"
              />
              <span className="text-slate-500">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
                placeholder="To Date"
                data-testid="date-to-input"
              />
            </div>
            <select
              value={selectedStore}
              onChange={(e) => setSelectedStore(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm bg-white dark:bg-slate-700 dark:text-white"
              data-testid="store-filter-select"
            >
              <option value="">All Stores</option>
              {data?.stores?.map(store => (
                <option key={store} value={store}>{store}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={loadData}
              data-testid="apply-filters-btn"
            >
              Apply Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-300">Total Visitors</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100" data-testid="total-visitors">
                  {formatNumber(summary.total_visitors || 0)}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">Total Sales</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100" data-testid="total-sales">
                  {formatCurrency(summary.total_sales || 0)}
                </p>
              </div>
              <IndianRupee className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 dark:text-purple-300">Transactions</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-testid="total-transactions">
                  {formatNumber(summary.total_transactions || 0)}
                </p>
              </div>
              <ShoppingCart className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700 dark:text-orange-300">Conversion Rate</p>
                <p className="text-2xl font-bold text-orange-900 dark:text-orange-100" data-testid="conversion-rate">
                  {summary.overall_conversion_rate || 0}%
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-50 to-cyan-100 dark:from-cyan-900/20 dark:to-cyan-800/20 border-cyan-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-cyan-700 dark:text-cyan-300">Revenue/Visitor</p>
                <p className="text-2xl font-bold text-cyan-900 dark:text-cyan-100" data-testid="revenue-per-visitor">
                  {formatCurrency(summary.revenue_per_visitor || 0)}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-cyan-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Store Comparison Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-blue-600" />
              Store-wise Footfall & Sales
            </CardTitle>
            <CardDescription>Visitors vs Revenue by Store</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="store_id" 
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  yAxisId="left" 
                  orientation="left" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <YAxis 
                  yAxisId="right" 
                  orientation="right" 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => formatCurrency(v)}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'Sales') return formatCurrency(value);
                    if (name === 'Visitors') return formatNumber(value);
                    return value;
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="visitors" name="Visitors" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" type="monotone" dataKey="sales" name="Sales" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
              </ComposedChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Conversion Rate Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Conversion Rate by Store
            </CardTitle>
            <CardDescription>Percentage of visitors who made a purchase</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="store_id" 
                  tick={{ fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  tick={{ fontSize: 11 }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip formatter={(value) => [`${value}%`, 'Conversion Rate']} />
                <Legend />
                <Bar 
                  dataKey="conversion_rate" 
                  name="Conversion Rate (%)" 
                  fill="#8b5cf6" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Enhanced Visualizations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Scatter Plot: Visitors vs Revenue Correlation */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-indigo-600" />
                Visitor-Revenue Correlation
              </CardTitle>
              <CardDescription>Each dot represents a store; size indicates conversion rate</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <ScatterChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    type="number" 
                    dataKey="visitors" 
                    name="Visitors" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatNumber(v)}
                    label={{ value: 'Visitors', position: 'bottom', fontSize: 12 }}
                  />
                  <YAxis 
                    type="number" 
                    dataKey="sales" 
                    name="Sales" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatCurrency(v)}
                    label={{ value: 'Revenue', angle: -90, position: 'insideLeft', fontSize: 12 }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Sales') return formatCurrency(value);
                      if (name === 'Visitors') return formatNumber(value);
                      if (name === 'Conversion') return `${value}%`;
                      return value;
                    }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div className="bg-white dark:bg-slate-800 p-3 rounded-lg shadow-lg border text-sm">
                          <p className="font-semibold">{d?.store_name || `Store ${d?.store_id}`}</p>
                          <p className="text-blue-600">Visitors: {formatNumber(d?.visitors || 0)}</p>
                          <p className="text-green-600">Revenue: {formatCurrency(d?.sales || 0)}</p>
                          <p className="text-purple-600">Conversion: {d?.conversion_rate || 0}%</p>
                          <p className="text-cyan-600">Rev/Visitor: {formatCurrency(d?.revenue_per_visitor || 0)}</p>
                        </div>
                      );
                    }}
                  />
                  <Scatter 
                    data={chartData.filter(d => d.visitors > 0)} 
                    fill="#6366f1" 
                    fillOpacity={0.7}
                    strokeWidth={1}
                    stroke="#4f46e5"
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Revenue per Visitor by Store */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="h-5 w-5 text-emerald-600" />
                Revenue Efficiency per Visitor
              </CardTitle>
              <CardDescription>How much revenue each visitor generates per store</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={[...chartData].filter(d => d.revenue_per_visitor > 0).sort((a, b) => b.revenue_per_visitor - a.revenue_per_visitor).slice(0, 12)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis 
                    dataKey="store_id" 
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => formatCurrency(v)}
                  />
                  <Tooltip formatter={(v) => [formatCurrency(v), 'Rev/Visitor']} />
                  <Legend />
                  <Bar 
                    dataKey="revenue_per_visitor" 
                    name="Revenue per Visitor" 
                    fill="#10b981" 
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Daily Trend Chart */}
      {data?.detailed_data?.length > 0 && (() => {
        const dailyMap = {};
        data.detailed_data.forEach(row => {
          if (!dailyMap[row.date]) dailyMap[row.date] = { date: row.date, visitors: 0, sales: 0, transactions: 0 };
          dailyMap[row.date].visitors += row.visitors || 0;
          dailyMap[row.date].sales += row.sales_value || 0;
          dailyMap[row.date].transactions += row.transactions || 0;
        });
        const trendData = Object.values(dailyMap).sort((a, b) => a.date.localeCompare(b.date));
        if (trendData.length < 2) return null;
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Daily Footfall & Sales Trend
              </CardTitle>
              <CardDescription>Aggregated daily visitors and revenue over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => formatNumber(v)} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Revenue') return formatCurrency(value);
                      return formatNumber(value);
                    }}
                  />
                  <Legend />
                  <Area yAxisId="left" type="monotone" dataKey="visitors" name="Visitors" stroke="#3b82f6" fill="url(#colorVisitors)" strokeWidth={2} />
                  <Area yAxisId="right" type="monotone" dataKey="sales" name="Revenue" stroke="#10b981" fill="url(#colorSales)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );
      })()}

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Detailed Footfall vs Sales Data</CardTitle>
          <CardDescription>
            Daily breakdown showing {data?.detailed_data?.length || 0} records
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="footfall-sales-table">
              <thead>
                <tr className="border-b bg-slate-50 dark:bg-slate-800">
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Store</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Date</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Visitors</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Sales</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Transactions</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Conversion</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Rev/Visitor</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Avg Txn</th>
                </tr>
              </thead>
              <tbody>
                {data?.detailed_data?.slice(0, 50).map((row, idx) => (
                  <tr 
                    key={idx} 
                    className="border-b hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">{row.store_id}</td>
                    <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{row.date}</td>
                    <td className="px-4 py-3 text-right">{row.visitors.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">
                      {formatCurrency(row.sales_value)}
                    </td>
                    <td className="px-4 py-3 text-right">{row.transactions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        row.conversion_rate >= 5 ? 'bg-green-100 text-green-700' :
                        row.conversion_rate >= 2 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {row.conversion_rate}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">{formatCurrency(row.revenue_per_visitor)}</td>
                    <td className="px-4 py-3 text-right">{formatCurrency(row.avg_transaction_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(data?.detailed_data?.length || 0) > 50 && (
            <p className="text-sm text-slate-500 mt-4 text-center">
              Showing 50 of {data?.detailed_data?.length} records. Export for full data.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FootfallVsSales;
